import { useState, useEffect } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import FloatingLabel from "react-bootstrap/FloatingLabel";

import Footer from "../components/Footer";
import Navbar from "../components/SpectraNavbar";

import axios from "axios";
import { useNavigate } from "react-router-dom";

export default function ResetPassword() {
  const [isVisible, setIsVisible] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    //redirect to login if no token present
    const token = window.location.pathname.split("/").pop();
    if (!token) {
      navigate("/login");
    }
    const validateToken = async () => {
      try {
        await axios.post(`/user/validate-reset-token`, { token });
      } catch (err) {
        // Pass error message via location state when redirecting
        navigate("/login", {
          state: {
            alert: {
              variant: "danger",
              message:
                err.response?.data?.code === "TOKEN_EXPIRED" ||
                err.response?.data?.code === "TOKEN_ALREADY_USED"
                  ? err.response.data.message
                  : "Invalid password reset link. Please request a new link.",
            },
          },
          replace: true,
        });
      }
    };
    validateToken();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Password validation regex (ASCII-only specials):
    // - Length 8-20
    // - At least 1 uppercase, 1 lowercase, 1 digit, 1 ASCII punctuation special
    // - Allowed characters: letters, digits, and ASCII punctuation only (no whitespace)
    const passwordRegex =
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!"#$%&'()*+,\-./:;<=>?@[\]\\^_`{|}~])[A-Za-z\d!"#$%&'()*+,\-./:;<=>?@[\]\\^_`{|}~]{8,20}$/;

    if (!passwordRegex.test(newPassword)) {
      setError(
        "Password must be 8-20 characters long, include one uppercase letter, one lowercase letter, one number, and one special character."
      );
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError("New passwords do not match");
      return;
    }

    setIsSubmitting(true);
    const token = window.location.pathname.split("/").pop();
    try {
      const response = await axios.patch(`/user/reset-password/${token}`, {
        newPassword,
      });
      if (response.status === 200) {
        setSuccess("Password reset successful. Redirecting to login...");
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setError(response.data.message || "Failed to reset password");
      }
    } catch (err) {
      if (err.response?.data?.code === "TOKEN_ALREADY_USED") {
        const message = err.response.data.message || "Reset link already used.";
        setError(`${message} Redirecting to login...`);
        setTimeout(() => {
          navigate("/login", {
            state: { alert: { variant: "danger", message } },
            replace: true,
          });
        }, 3000);
        return;
      }
      if (err.response?.data?.code === "TOKEN_EXPIRED") {
        const message =
          "Reset link has expired. Please request a new password reset link.";
        setError(`${message} Redirecting to login...`);
        setTimeout(() => {
          navigate("/login", {
            state: { alert: { variant: "danger", message } },
            replace: true,
          });
        }, 3000);
        return;
      }
      setError("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShowPassword = () => {
    setIsVisible(!isVisible);
  };

  return (
    <div
      id='main-wrapper'
      className='d-flex flex-column container-fluid bg-light min-vh-100'
    >
      <Navbar />
      <div
        className='d-flex justify-content-center align-items-center container-fluid flex-grow-1 h-100'
        style={{ paddingTop: "79px" }}
      >
        <div id='login-form' className='col-lg-8 col-12  bg-white p-5 border'>
          <header className='mb-4 text-center'>
            <h1 className='mb-1'>Reset Password</h1>
            <p className='text-muted fs-5'>Enter your new password</p>
            {error && <div className='alert alert-danger'>{error}</div>}
            {success && <div className='alert alert-success'>{success}</div>}
          </header>
          <Form noValidate className='my-3'>
            <Form.Group className='mb-3' controlId='formBasicEmail'>
              <FloatingLabel label='New Password'>
                <Form.Control
                  className='login-input'
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete='new-password'
                  type={isVisible ? "text" : "password"}
                  placeholder='New Password'
                  required
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group className='mb-1' controlId='formBasicPassword'>
              <FloatingLabel label='Confirm New Password'>
                <Form.Control
                  type={isVisible ? "text" : "password"}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  autoComplete='new-password'
                  placeholder='Confirm New Password'
                  required
                />
              </FloatingLabel>
            </Form.Group>

            <div className='d-flex justify-content-between align-items-center'>
              <Form.Check
                type='checkbox'
                label='Show Password'
                id='show-password-checkbox'
                onChange={handleShowPassword}
                className='ms-2 w-50'
              />
            </div>
            <div className='d-flex justify-content-between align-items-center'>
              <p className='text-muted mt-2' style={{ fontSize: "0.9rem" }}>
                Password must be at least 8 characters long, include one
                uppercase letter, one lowercase letter, one number, and one
                special character.
              </p>
            </div>

            <div className='d-grid'>
              {success === "" && (
                <Button
                  variant='primary'
                  className='mt-4 w-50 mx-auto'
                  type='submit'
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  Submit{" "}
                </Button>
              )}
            </div>
          </Form>
        </div>
      </div>
      <Footer />
    </div>
  );
}
